<template>
    <b-row class="my-3">
      <b-col cols="12" sm="12">
        <div class="d-flex justify-content-between text-right pb-2" align-h="between">
          <div class="d-flex justify-content-between">
            <p><b>{{ totalJobsSelected }}</b> {{ $t('campaign-admin.labels.selected') }}</p>
          </div>

          <div>
            <!-- Create Job Button -->
            <b-button size="md" variant="outline-primary" v-b-modal.new-job-by-url-modal>
              <font-awesome-icon icon="plus"/>
              <span class="d-inline d-sm-inline-block">{{ $t('content-fetch.titles.create-job') }}</span>
            </b-button>

            <!-- Select Existing Jobs Button -->
            <entry-where-some-input
              type="jobs"
              :title="$t('navigation.jobs')"
              many
              :placeholder="$t('campaign-admin.placeholders.select-jobs')"
              :buttonView="true"
              :where="{ status: 'ACTIVE' }"
              order="CREATED_DESC"
              v-model="selectedJobs"
              :buttonText="$t('campaign-admin.buttons.select-jobs')"
              buttonIcon="sync"
            />
          </div>

          <!-- Create Job Modal -->
          <b-modal
            id="new-job-by-url-modal"
            size="xl"
            no-close-on-backdrop
            hide-footer
            :title="$t('content-fetch.titles.create-job')"
          >
            <content-fetch @input="createNewJob($event)"/>
          </b-modal>
        </div>
        <hr class="d-sm-none"/>
        <!-- No Jobs Selected Statement -->
        <h4 class="my-4 py-4 text-center" v-if="totalJobsSelected < 1">
          <strong>{{ $t('campaign-admin.labels.no-jobs-selected') }}</strong>
        </h4>
        <b-row>
          <b-col class="px-3 draggable">
            <draggable
              v-model="selectedJobs"
              handle=".bars-handle"
              class="mb-4"
            >
              <transition-group>
                <div
                class="d-flex justify-content-between mb-2 border border-gray-600 rounded bg-white"
                size="lg"
                v-for="job in selectedJobs"
                :key="job.id"
                >
                  <!-- Job Title -->
                  <span class="py-2 pl-3" v-b-modal="`job-description-modal-${job.id}`">
                    {{ truncateJobTitle(job.title) }}
                  </span>
                  <div class="d-flex justify-content-between align-items-center">
                    <b-link @click="removeJob(job.id)" class="mr-2 text-danger">{{ $t('campaign-admin.buttons.delete') }}</b-link>
                    <!-- Bars Icon -->
                    <span class="bars-handle">
                      <font-awesome-icon class="draggable-icon mr-3 hamburger-icon" icon="bars"/>
                    </span>
                  </div>
                  <!-- Job Description Modal -->
                  <b-modal :id="`job-description-modal-${job.id}`" :title="job.title" hide-footer lazy size="lg">
                    <key-stats class="mb-3 w-100" :job="job"/>
                    <markdown :source="job.description" :anchorAttributes="markdownAnchorAttributes"/>
                  </b-modal>
                </div>
              </transition-group>
            </draggable>
          </b-col>
        </b-row>
      </b-col>
      <b-col class="d-flex flex-row justify-content-start align-items-center" cols="12">
        <b-btn @click="back" variant="transparent">{{ $t('campaign.scheduled-posts.back') }}</b-btn>
        <div class="flex-fill"/>
        <b-btn class="mr-2" @click="saveSelection" variant="primary">{{ $t('campaign-admin.buttons.save-selection') }}</b-btn>
      </b-col>
    </b-row>
</template>

<script>
import _isEqual from 'lodash/isEqual';
import ContentFetch from '@/components/misc/ContentFetch.vue';
import EntryWhereSomeInput from '@src/components/manage/EntryWhereSomeInput';
import JobDetailsFragment from '@/graphql/fragments/JobDetails.gql';
import draggable from 'vuedraggable';
import Markdown from 'vue-markdown';
import KeyStats from '@src/components/jobs/key-stats';
import gql from 'graphql-tag';
import LocationSearch from '@/components/misc/LocationSearch';

const NewJob = gql`
  mutation JobNew($create: CreateJobInput!) {
    createJob(create: $create) {
      ...JobDetails
    }
  }
  ${JobDetailsFragment}
`;

export default {
  name: 'CampaignAdminJobs',
  props: {
    value: {
      type: Array,
    },
  },
  components: {
    Markdown,
    KeyStats,
    LocationSearch,
    EntryWhereSomeInput,
    ContentFetch,
    draggable,
  },
  data: () => ({
    selectedJobs: null,
    initialSelectedJobs: null,
  }),
  created() {
    this.selectedJobs = this.value;
    this.initialSelectedJobs = JSON.parse(JSON.stringify(this.value));
  },
  computed: {
    markdownAnchorAttributes: () => ({
      // Open all hyperlinks in the Markdown block in a new tab
      target: '_blank',
      // Enforce a nofollow to avoid SEO backlash from private content
      rel: 'nofollow',
    }),
    totalJobsSelected() {
      const { selectedJobs } = this;
      return Array.isArray(selectedJobs) ? selectedJobs.length : 0;
    },
    unsavedChanges() {
      return !_isEqual(this.initialSelectedJobs, this.selectedJobs);
    },
  },
  methods: {
    truncateJobTitle(jobTitle) {
      if (jobTitle.length < 40) {
        return jobTitle;
      } else {
        return jobTitle.substring(0, 40).concat('...');
      }
    },
    removeJob(jobId) {
      const { selectedJobs } = this;
      const index = selectedJobs.findIndex(job => job.id === jobId);
      if (index > -1) {
        selectedJobs.splice(index, 1);
      }
    },
    saveSelection() {
      const { selectedJobs } = this;
      this.initialSelectedJobs = JSON.parse(JSON.stringify(selectedJobs));
      this.$emit('saveSelection', selectedJobs);
    },
    back() {
      this.$emit('back');
    },
    async createNewJob(job) {
      try {
        const { data } = await this.$apollo.mutate({
          mutation: NewJob,
          variables: { create: job }
        });
        const { createJob: newJob } = data || {};
        console.log({ newJob });
        if (Array.isArray(this.selectedJobs)) {
          this.selectedJobs.push(newJob);
        } else {
          this.selectedJobs = [ newJob ];
        }
      } catch (err) {
        console.log(err);
      }
    }
  },
};
</script>
<style lang="scss" scoped>
  .draggable-icon {
    font-size: 1.3rem;
  }
  .bg-bar-color {
    background-color: #dee2e6;
  }
  .inactiveButton {
    color: #001DC0;
    text-decoration: underline;
  }
  .activeButton {
    color: white;
    background-color: #001DC0;
  }
  .activeButton:hover {
    background-color: red;
    color: white;
  }
  .inactiveButton:hover {
    color: white;
    background-color: #001DC0;
  }
  .hamburger-icon:hover {
    cursor: move;
  }
  .hidden {
    visibility: hidden;
  }
  .link-styling {
    text-decoration: underline;
    cursor: pointer;
  }
</style>



// WEBPACK FOOTER //
// src/components/campaign-admin/update-selected-jobs.vue